import { useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { coopInvestigationUrl } from '@/lib/moderationToolLinks';
import { cn } from '@/lib/utils';
import {
  formatCoopScanBadge,
  isDeepScanSettled,
  isProvisionalNoAction,
} from '../lib/verdictDisplay';
import {
  formatCoopScanStatus,
  isAwaitingCaseDelivery,
  normalizeDeliveryStatus,
} from '../lib/coopDelivery';
import { CoopScanDetails } from './CoopScanDetails';
import { useTickingNow } from '../lib/useTickingNow';
import type { ModerationIntelligence } from '../types';
import { SignalScoreBar } from './SignalScoreBar';
import { RuleTraceTable } from './RuleTraceTable';
import { PipelineTimeline } from './PipelineTimeline';

const VERDICT_BADGE_COLOR: Record<string, string> = {
  APPROVE: 'bg-green-100 text-green-800 border-green-200',
  REJECT: 'bg-red-100 text-red-800 border-red-200',
  ESCALATE: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  HUMAN_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  BYPASSED: 'bg-green-100 text-green-800 border-green-200',
};

const IN_PROGRESS_STYLE = 'bg-sky-100 text-sky-800 border-sky-200';

function verdictBadgeClass(verdict: string | null, provisional = false): string {
  if (provisional) return IN_PROGRESS_STYLE;
  if (!verdict) return '';
  const upper = verdict.toUpperCase();
  for (const [key, cls] of Object.entries(VERDICT_BADGE_COLOR)) {
    if (upper.includes(key)) return cls;
  }
  return 'bg-muted text-muted-foreground';
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown className={cn('size-3.5 transition-transform', open ? '' : '-rotate-90')} />
        {title}
      </button>
      {open && children}
    </div>
  );
}

function IntelligenceSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-6 w-20 rounded-md" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
        <Skeleton className="h-3 w-3/5" />
        <Skeleton className="h-20 w-full" />
      </CardContent>
    </Card>
  );
}

export function ModerationIntelligenceCard({
  intelligence,
  isLoading,
  moderationStatus,
  hideVerdictReason = false,
}: {
  intelligence: ModerationIntelligence | null;
  isLoading: boolean;
  moderationStatus?: string | null | undefined;
  /** When true, skip the top verdict_reason block (e.g. content detail already shows AI summary). */
  hideVerdictReason?: boolean;
}) {
  const awaitingDelivery = isAwaitingCaseDelivery(
    intelligence?.delivery_status,
    intelligence?.case_status,
  );
  const nowMs = useTickingNow(awaitingDelivery);

  if (isLoading) return <IntelligenceSkeleton />;
  const ledgerPending =
    (moderationStatus ?? '').trim().toUpperCase() === 'PENDING' && !intelligence;
  const deliveryFailed =
    normalizeDeliveryStatus(intelligence?.delivery_status) === 'failed' &&
    !(intelligence?.signals?.length) &&
    !(intelligence?.verdict);

  if (!intelligence || awaitingDelivery || ledgerPending || deliveryFailed) {
    // Only fabricate Opening from a real outbox stub (HOLD/LIVE_ENFORCE).
    // OBSERVE leaves ledger PENDING with no case/outbox — do not imply delivery.
    const coopStatus = formatCoopScanStatus({
      deliveryStatus: intelligence?.delivery_status ?? null,
      deliveryLastError: intelligence?.delivery_last_error ?? intelligence?.verdict_reason,
      deliveryLastStage: intelligence?.delivery_last_stage,
      deliveryAttemptCount: intelligence?.delivery_attempt_count,
      deliveryLastAttemptAt: intelligence?.delivery_last_attempt_at,
      deliveryNextAttemptAt: intelligence?.delivery_next_attempt_at,
      nowMs,
    });
    const label =
      coopStatus.phase !== 'none'
        ? coopStatus.label
        : deliveryFailed
          ? 'Coop · Delivery failed'
          : awaitingDelivery
            ? 'Coop · Opening'
            : 'No Coop case';
    const secondary =
      coopStatus.details.length > 0
        ? null
        : ledgerPending
          ? 'TipMi ledger is PENDING (observe / no Coop case on create). Signals appear only when a Coop case exists.'
          : awaitingDelivery
            ? 'Waiting for media-manager case — signals and rule trace will appear when delivery completes.'
            : 'Never opened or unavailable — signals and rule trace will appear when a Coop case exists.';
    const awaitingStyle = awaitingDelivery;
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm">Coop intelligence</CardTitle>
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              awaitingStyle
                ? IN_PROGRESS_STYLE
                : deliveryFailed
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'border-dashed text-muted-foreground',
            )}
          >
            {label}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          <CoopScanDetails details={coopStatus.details} />
          {secondary ? (
            <p className="text-xs text-muted-foreground break-words">{secondary}</p>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const openInCoopHref = coopInvestigationUrl(intelligence.case_id, intelligence.coop_url);
  const deepScanSettled = isDeepScanSettled({
    deepScanDurationMs: intelligence.deep_scan_duration_ms,
    deliveryStatus: intelligence.delivery_status,
    vectors: intelligence.vectors,
  });
  const provisional = isProvisionalNoAction(intelligence.verdict, intelligence.case_status, {
    deepScanSettled,
  });
  const badgeLabel = formatCoopScanBadge({
    caseStatus: intelligence.case_status,
    deepScanSettled,
    enforcementActions: intelligence.enforcement_actions,
    enforcementPreviews: intelligence.enforcement_previews,
    verdict: intelligence.verdict,
  });
  const badgeProvisional = provisional || badgeLabel === 'Coop · Scanning';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Coop intelligence</CardTitle>
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            verdictBadgeClass(badgeLabel, badgeProvisional),
          )}
        >
          {badgeLabel}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-5">
        {!hideVerdictReason && intelligence.verdict_reason && (
          <p className="whitespace-pre-wrap text-xs text-muted-foreground">{intelligence.verdict_reason}</p>
        )}

        {/* Signal Scores */}
        {(intelligence.signals?.length ?? 0) > 0 && (
          <Section title="Signal Scores">
            <div className="space-y-2">
              {(intelligence.signals ?? []).map((s) => (
                <SignalScoreBar
                  key={s.signal_id}
                  label={s.display_name}
                  score={s.score}
                  threshold={s.threshold}
                />
              ))}
            </div>
          </Section>
        )}

        {/* TipMi content-graph rule preview — not live Coop ACTION_EXECUTIONS */}
        {(intelligence.rule_evaluations?.length ?? 0) > 0 && (
          <Section title="TipMi rule preview" defaultOpen={false}>
            <RuleTraceTable evaluations={intelligence.rule_evaluations ?? []} />
          </Section>
        )}

        {/* Pipeline Timeline */}
        {(intelligence.pipeline_steps?.length ?? 0) > 0 && (
          <Section title="Pipeline" defaultOpen={false}>
            <PipelineTimeline steps={intelligence.pipeline_steps ?? []} />
          </Section>
        )}

        {/* External link */}
        {openInCoopHref && (
          <a
            href={openInCoopHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-full')}
          >
            Open in Coop
            <ExternalLink className="ml-2 size-3.5" />
          </a>
        )}
      </CardContent>
    </Card>
  );
}
